<div class="control-panel-container">
  <header class="ptl-content-header control-panel__header">
    <div class="control-panel__heading">
      <h1 class="control-panel__title">{{ 'translations.members.title' | transloco }}</h1>
      <p class="control-panel__description">
        @if (isAdminScope) {
          {{ 'translations.admin.members.subtitle' | transloco }}
        } @else {
          {{
            allMembersCount() ?? 0
              | i18nPlural
                : {
                    '=1': 'translations.global.label.oneUser' | transloco,
                    other:
                      'translations.global.label.countUsers'
                      | transloco
                        : {
                            count: allMembersCount() ?? 0,
                          },
                  }
          }}
        }
      </p>
    </div>

    <div class="ptl-content-header__actions control-panel__actions">
      @if (exportEnabled() && !isIframeMode) {
        @if (advancedExportAvailable) {
          <ptl-button
            type="secondary-neutral"
            size="sm"
            [icon]="LucideDownload"
            [iconSuffix]="LucideChevronDown"
            [text]="'translations.membersNew.controlPanel.export' | transloco"
            [matMenuTriggerFor]="exportMenu"
            [attr.aria-label]="'translations.global.ariaLabels.export' | transloco"
          ></ptl-button>
        } @else {
          <ptl-button
            type="secondary-neutral"
            size="sm"
            [icon]="LucideDownload"
            [text]="'translations.membersNew.controlPanel.export' | transloco"
            (clicked)="exportUsersButtonClicked()"
            [attr.aria-label]="'translations.global.ariaLabels.exportUsers' | transloco"
          ></ptl-button>
        }
      }

      @if (exportEnabled() && isIframeMode) {
        <ptl-button
          type="secondary-neutral"
          size="sm"
          [icon]="LucideDownload"
          [text]="'translations.membersNew.controlPanel.export' | transloco"
          (clicked)="exportUsersFilteredByGroup()"
          [attr.aria-label]="'translations.global.ariaLabels.exportUsers' | transloco"
        ></ptl-button>
      }

      @if (!isIframeMode && (isCardPage() || isPlaylistPage())) {
        <ptl-members-re-index-button
          [playlistUid]="playlistUid()"
          [cardUid]="isCardPage() ? cardUid() : null"
          (completed)="refreshList.emit(0)"
        ></ptl-members-re-index-button>
      }

      @if (!isIframeMode && enrolsOnCourse()) {
        @if (enrolCourse(); as course) {
          <ptl-v3-enrol-people-menu
            labelKey="translations.adminEnrolment.menu.enrol"
            buttonType="secondary-neutral"
            size="sm"
            [playlistUid]="course.uid"
            [contentTitle]="course.title"
            [published]="course.published"
            (enrolled)="refreshList.emit(0)"
          ></ptl-v3-enrol-people-menu>
        }
      } @else if (!isIframeMode && (isEvent() || isCardPage() || isPlaylistPage())) {
        <ptl-button
          type="secondary-neutral"
          size="sm"
          [icon]="LucideUpload"
          [text]="'translations.members.button.importCsv' | transloco"
          [disabled]="isImportDisabled"
          [matTooltip]="importDisabledTooltipText ?? ''"
          matTooltipClass="medium-tooltip"
          (clicked)="importUsersCsv()"
        ></ptl-button>
      }

      @if (!isIframeMode && isAdminScope && (canManageUserImportPrivileges$ | async)) {
        <ptl-button
          type="primary"
          size="sm"
          [icon]="LucidePlus"
          [text]="'translations.members.button.inviteMember' | transloco"
          (clicked)="onHeaderInviteMember()"
        ></ptl-button>
      }
    </div>
  </header>

  @if (showInviteMembers()) {
    <ptl-v3-invite-member (closed)="onQuickInviteClosed()" (invitationSent)="onQuickInviteSent()"></ptl-v3-invite-member>
  }

  @if (isEvent() || isCardPage() || isPlaylistPage()) {
    <div class="control-panel__context">
      <div class="description-remaining-tickets-box">
        @if (isEvent()) {
          <span class="body-s description">
            {{ 'translations.membersNew.controlPanel.pageDescriptionEvent' | transloco }}
          </span>
        }
        @if ((isCardPage() || isPlaylistPage()) && !isEvent()) {
          <div class="description">
            <span class="body-s">
              {{ 'translations.membersNew.controlPanel.pageDescriptionCardPlaylist.first' | transloco }}
            </span>
            <span class="body-s bold">
              {{ 'translations.membersNew.controlPanel.pageDescriptionCardPlaylist.second' | transloco }}
            </span>
            <span class="body-s">
              {{ 'translations.membersNew.controlPanel.pageDescriptionCardPlaylist.third' | transloco }}
            </span>
            <span class="body-s bold">
              {{ 'translations.membersNew.controlPanel.pageDescriptionCardPlaylist.fourth' | transloco }}
            </span>
            <span class="body-s">
              {{ 'translations.membersNew.controlPanel.pageDescriptionCardPlaylist.fifth' | transloco }}
            </span>
            <span class="body-s bold">
              {{ 'translations.membersNew.controlPanel.pageDescriptionCardPlaylist.sixth' | transloco }}
            </span>
            <span class="body-s">
              {{ 'translations.membersNew.controlPanel.pageDescriptionCardPlaylist.seventh' | transloco }}
            </span>
          </div>
        }
        @if (limitedTickets) {
          <div class="remaining-tickets-count">
            <span class="body-s bold" [class]="remainingTicketsInfoClass"> {{ getRemainingTickets() }} / </span>
            <span class="body-s">
              {{ 'translations.membersNew.controlPanel.ticketsLeft' | transloco: { ticketsQuantity: ticketsQuantity } }}
            </span>
          </div>
        }
      </div>
    </div>
  }

  <div class="members-toolbar">
    <mat-menu #exportMenu>
      <button mat-menu-item (click)="exportUsersButtonClicked()">
        <span class="body-s">{{ 'translations.members.button.exportCsv' | transloco }}</span>
      </button>
      <mat-divider></mat-divider>
      <button mat-menu-item (click)="openAdvancedExport()">
        <span class="body-s">{{ 'translations.advancedExport.title.main' | transloco }}</span>
      </button>
    </mat-menu>

    <div class="members-toolbar__row">
      <ptl-sg-input
        [clearable]="true"
        class="members-toolbar__search"
        size="s"
        [withIcon]="true"
        [icon]="LucideSearch"
        type="text"
        [formControl]="searchControl"
        [placeholder]="'translations.programs.members.searchPlaceholder' | transloco"
      ></ptl-sg-input>

      <ng-content select="[toolbar-actions]"></ng-content>

      <ptl-dropdown
        align="end"
        [open]="filterMenuOpen()"
        (openChange)="filterMenuOpen.set($event)"
        (keydown.escape)="filterMenuOpen.set(false)"
      >
        <ptl-button
          trigger
          [size]="'s'"
          [type]="'secondary'"
          [text]="'translations.global.button.filter' | transloco"
          [ariaExpanded]="filterMenuOpen()"
          [attr.aria-haspopup]="'menu'"
          (clicked)="filterMenuOpen.set(!filterMenuOpen())"
        ></ptl-button>

        <div class="members-filter-menu" role="menu">
          @for (label of filterLabels; track label.type) {
            <button type="button" class="members-filter-menu__item" role="menuitem" (click)="onFilterMenuItemClick(label.type)">
              {{ label.value }}
            </button>
          }
        </div>
      </ptl-dropdown>
    </div>

    @if (filters?.length || newFilter) {
      <div class="filter-chips-list">
        @for (filter of filters; track filter.type) {
          <div class="filter-chip-wrapper">
            <ptl-base-badge
              [text]="filter.message"
              [color]="'blue'"
              [iconSuffix]="LucideX"
              [matTooltip]="filter.message"
              (clicked)="filterSelected(filter.type)"
              (iconClick)="onRemoveFilter(filter.type)"
            ></ptl-base-badge>
            @if (currentFilter === filter.type) {
              @switch (filter.type) {
                @case ('REVIEW_STATUS') {
                  <ptl-v3-review-status-filter
                    class="filter-panel"
                    (filterDeleted)="onRemoveFilter('REVIEW_STATUS')"
                    (filterSaved)="onSaveFilter($event)"
                    [selectedFilters]="filters"
                  ></ptl-v3-review-status-filter>
                }
                @case ('GROUP') {
                  <ptl-v3-group-filter
                    class="filter-panel"
                    (filterDeleted)="onRemoveFilter('GROUP')"
                    (filterSaved)="onSaveFilter($event)"
                    [selectedFilters]="filters"
                    [playlistUid]="!isAdminPage() ? playlistUid() : undefined"
                  ></ptl-v3-group-filter>
                }
                @case ('ROLE') {
                  <ptl-v3-role-filter
                    class="filter-panel"
                    (filterDeleted)="onRemoveFilter('ROLE')"
                    (filterSaved)="onSaveFilter($event)"
                    [selectedFilters]="filters"
                  ></ptl-v3-role-filter>
                }
                @case ('ATTENDANCE') {
                  <ptl-v3-attendance-filter
                    class="filter-panel"
                    (filterDeleted)="onRemoveFilter('ATTENDANCE')"
                    (filterSaved)="onSaveFilter($event)"
                    [selectedFilters]="filters"
                    [isPlaylistPageAndTicketExist]="isPlaylistPage() && isTicketExist"
                  ></ptl-v3-attendance-filter>
                }
                @case ('ACTIVITY') {
                  <ptl-v3-activity-date-filter
                    class="filter-panel"
                    (filterDeleted)="onRemoveFilter('ACTIVITY')"
                    (filterSaved)="onSaveFilter($event)"
                    [selectedFilters]="filters"
                  ></ptl-v3-activity-date-filter>
                }
                @case ('COMPLETION') {
                  <ptl-v3-completion-filter
                    class="filter-panel"
                    (filterDeleted)="onRemoveFilter('COMPLETION')"
                    (filterSaved)="onSaveFilter($event)"
                    [selectedFilters]="filters"
                    [isPlaylistPage]="isPlaylistPage()"
                    [playlistUid]="playlistUid()"
                    [isCardPage]="isCardPage()"
                    [cardUid]="cardUid()"
                    [cardHeaders]="cardHeaders()"
                    [formHeaders]="formHeaders()"
                  ></ptl-v3-completion-filter>
                }
              }
            }
          </div>
        }

        @if (newFilter) {
          <div class="filter-chip-wrapper">
            @switch (newFilter) {
              @case ('GROUP') {
                @if (!isIframeMode) {
                  <ptl-v3-group-filter
                    class="filter-panel"
                    (filterDeleted)="onRemoveFilter('GROUP')"
                    (filterSaved)="onSaveFilter($event)"
                    [selectedFilters]="filters"
                    [playlistUid]="!isAdminPage() ? playlistUid() : undefined"
                  ></ptl-v3-group-filter>
                }
              }
              @case ('ROLE') {
                <ptl-v3-role-filter
                  class="filter-panel"
                  (filterDeleted)="onRemoveFilter('ROLE')"
                  (filterSaved)="onSaveFilter($event)"
                  [selectedFilters]="filters"
                ></ptl-v3-role-filter>
              }
              @case ('ATTENDANCE') {
                <ptl-v3-attendance-filter
                  class="filter-panel"
                  (filterDeleted)="onRemoveFilter('ATTENDANCE')"
                  (filterSaved)="onSaveFilter($event)"
                  [selectedFilters]="filters"
                  [isPlaylistPageAndTicketExist]="isPlaylistPage() && isTicketExist"
                ></ptl-v3-attendance-filter>
              }
              @case ('ACTIVITY') {
                <ptl-v3-activity-date-filter
                  class="filter-panel"
                  (filterDeleted)="onRemoveFilter('ACTIVITY')"
                  (filterSaved)="onSaveFilter($event)"
                  [selectedFilters]="filters"
                ></ptl-v3-activity-date-filter>
              }
              @case ('COMPLETION') {
                <ptl-v3-completion-filter
                  class="filter-panel"
                  (filterDeleted)="onRemoveFilter('COMPLETION')"
                  (filterSaved)="onSaveFilter($event)"
                  [selectedFilters]="filters"
                  [isPlaylistPage]="isPlaylistPage()"
                  [playlistUid]="playlistUid()"
                  [isCardPage]="isCardPage()"
                  [cardUid]="cardUid()"
                  [cardHeaders]="cardHeaders()"
                  [formHeaders]="formHeaders()"
                ></ptl-v3-completion-filter>
              }
              @case ('REVIEW_STATUS') {
                <ptl-v3-review-status-filter
                  class="filter-panel"
                  (filterDeleted)="onRemoveFilter('REVIEW_STATUS')"
                  (filterSaved)="onSaveFilter($event)"
                  [submissionMode]="getSubmissionMode"
                  [selectedFilters]="filters"
                ></ptl-v3-review-status-filter>
              }
            }
          </div>
        }
      </div>
    }
  </div>
</div>
